import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Button, Collapse, Space, Typography } from 'antd';
import { UpOutlined } from '@ant-design/icons';
import {
  faBuilding,
  faBus,
  faClipboardList,
  faClock,
  faFilter,
  faGlobe,
  faHome,
  faMapMarkerAlt,
  faRulerHorizontal,
  faShoppingBag,
  faStopwatch,
  faUsers,
  faVenusMars,
} from '@fortawesome/free-solid-svg-icons';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import { ACTIVITY_TYPES } from '../../../constants/activityType';
import { AGE_GROUPS, type AgeGroup } from '../../../constants/ageGroup';
import { TRAVEL_MODES } from '../../../constants/travelMode';
import { includes } from '../../../utils/array';
import {
  formatDistance,
  formatDuration,
  formatTime,
} from '../../../utils/format';
import { useMetaData } from '../../../hooks/data/useMetaData';
import { useCustomAreas } from '../../../hooks/SimViewer/useCustomAreas';
import { useAreasData } from '../../../hooks/data/useAreasData';
import { useLinksData } from '../../../hooks/data/useLinksData';
import { useDataFilter } from '../../../hooks/SimViewer/useDataFilter';
import { useAggregationConfig } from '../../../hooks/SimViewer/useAggregationConfig';
import { scrollbarStyle } from '../../../styles/scrollbar';
import { FeatureIcon } from '../SimViewerSideMenu/FeatureIcon';
import { FilterContentGroup } from './FilterContentGroup';

const { Text } = Typography;

const agentFilters = [
  { type: 'home', labelKey: 'home', icon: faHome },
  { type: 'work', labelKey: 'work', icon: faBuilding },
  { type: 'visit', labelKey: 'visit', icon: faMapMarkerAlt },
] as const satisfies readonly {
  type: string;
  labelKey: string;
  icon: IconProp;
}[];
const tripFilters = [
  { type: 'start', labelKey: 'tripStart', icon: faMapMarkerAlt },
  { type: 'end', labelKey: 'tripEnd', icon: faMapMarkerAlt },
  { type: 'pass', labelKey: 'tripPass', icon: faMapMarkerAlt },
] as const satisfies readonly {
  type: string;
  labelKey: string;
  icon: IconProp;
}[];

const SideMenuFilter = (): ReactNode => {
  const { t } = useTranslation();
  const { currentDataMeta } = useMetaData();
  const {
    dataFilter,
    agentFilterCount,
    tripFilterCount,
    advancedFilterCount,
    clearAllFilters,
    deleteAreaFilter,
    deleteCustomAreaFilter,
    deleteLinkFilter,
    deleteAttributeFilter,
    setRangeFilter,
    setPassRelation,
    setStopBy,
  } = useDataFilter();
  const { getCustomAreaByKey } = useCustomAreas();
  const { findArea } = useAreasData();
  const { links } = useLinksData();
  const { clearAggregation } = useAggregationConfig();

  if (agentFilterCount + tripFilterCount + advancedFilterCount === 0) {
    return (
      <Text disabled style={{ paddingInline: '1rem' }}>
        {t('message.noFiltersApplied')}
      </Text>
    );
  }
  return (
    <Collapse
      defaultActiveKey={['currentFilter']}
      ghost
      expandIcon={({ isActive }: { isActive?: boolean }): ReactNode => {
        return <UpOutlined rotate={isActive ? 0 : 180} />;
      }}
      expandIconPosition="right"
    >
      <StyledPanel
        key="currentFilter"
        header={
          <Space>
            <FeatureIcon icon={faFilter} />
            {t('currentFilters')}
            <span>
              ({agentFilterCount + tripFilterCount + advancedFilterCount})
            </span>
            <Button
              size="small"
              onClick={(): void => {
                clearAllFilters();
                clearAggregation();
              }}
            >
              {t('clearAll')}
            </Button>
          </Space>
        }
      >
        {agentFilterCount + tripFilterCount + advancedFilterCount >= 2 && (
          <FilterGroupingBracket>{t('and')}</FilterGroupingBracket>
        )}
        <Space direction="vertical" size={24}>
          {agentFilterCount > 0 && (
            <div>
              <FilterHeader>{t('agentAnalysis')}</FilterHeader>
              <Space direction="vertical" size={8}>
                <FilterContentGroup
                  title={t('gender')}
                  icon={<FeatureIcon icon={faVenusMars} size={12} />}
                  contents={dataFilter.genderIds.map((genderId: string) => {
                    return {
                      key: genderId,
                      label:
                        genderId === '1'
                          ? t('genders.male')
                          : t('genders.female'),
                      onClose: (): void => {
                        deleteAttributeFilter('genderIds', genderId);
                      },
                    };
                  })}
                  relation="or"
                />
                <FilterContentGroup
                  title={t('ageGroup')}
                  icon={<FeatureIcon icon={faClipboardList} size={12} />}
                  contents={[...dataFilter.ageGroups]
                    .filter((ageGroup: string): ageGroup is AgeGroup => {
                      return includes(AGE_GROUPS, ageGroup);
                    })
                    .sort((age1: AgeGroup, age2: AgeGroup): number => {
                      // 年齢の若い順に並び替える
                      return age1.localeCompare(age2, 'en');
                    })
                    .map((ageGroup: AgeGroup) => {
                      return {
                        key: ageGroup,
                        label:
                          currentDataMeta?.type === 'gad_standard' &&
                          ageGroup === '60'
                            ? t(`ageGroups.60s+`)
                            : t(`ageGroups.${ageGroup}s`),
                        onClose: (): void => {
                          deleteAttributeFilter('ageGroups', ageGroup);
                        },
                      };
                    })}
                  relation="or"
                />
                <FilterContentGroup
                  title={t('nationality')}
                  icon={<FeatureIcon icon={faGlobe} size={12} />}
                  contents={dataFilter.nationalities.map(
                    (nationality: string) => {
                      return {
                        key: nationality,
                        label: t(`countries.${nationality}`, {
                          defaultValue: t('countries.ZZZ'),
                        }),
                        onClose: (): void => {
                          deleteAttributeFilter('nationalities', nationality);
                        },
                      };
                    }
                  )}
                  relation="or"
                />
                {agentFilters.map(({ type, labelKey, icon }): ReactNode => {
                  if (
                    dataFilter[type].areaKeyCodes.length === 0 &&
                    dataFilter[type].customAreaKeys.length === 0
                  ) {
                    return null;
                  }
                  return (
                    <FilterContentGroup
                      key={type}
                      title={t(labelKey)}
                      icon={<FeatureIcon icon={icon} size={12} />}
                      contents={[
                        ...dataFilter[type].areaKeyCodes.map(
                          (keyCode: string) => {
                            return {
                              key: `${type}-area-${keyCode}`,
                              label: findArea(keyCode)?.s_name ?? '',
                              onClose: (): void => {
                                deleteAreaFilter(keyCode, type);
                              },
                            };
                          }
                        ),
                        ...dataFilter[type].customAreaKeys.map(
                          (key: string) => {
                            return {
                              key: `${type}-custom-area-${key}`,
                              label: getCustomAreaByKey(key)?.label || '',
                              onClose: (): void => {
                                deleteCustomAreaFilter(key, type);
                              },
                            };
                          }
                        ),
                      ]}
                      relation="or"
                    />
                  );
                })}
              </Space>
            </div>
          )}
          {tripFilterCount > 0 && (
            <div>
              <FilterHeader>{t('tripAnalysis')}</FilterHeader>
              <Space direction="vertical" size={8}>
                {dataFilter.endActivityTypes.length > 0 && (
                  <FilterContentGroup
                    title={t('endActivity')}
                    icon={<FeatureIcon icon={faShoppingBag} size={12} />}
                    contents={dataFilter.endActivityTypes.map(
                      (activityType: string) => {
                        return {
                          key: activityType,
                          label: includes(ACTIVITY_TYPES, activityType)
                            ? t(`activityType.${activityType}`)
                            : activityType,
                          onClose: (): void => {
                            deleteAttributeFilter(
                              'endActivityTypes',
                              activityType
                            );
                          },
                        };
                      }
                    )}
                    relation="or"
                  />
                )}
                <FilterContentGroup
                  title={t('longestDistanceMode')}
                  icon={<FeatureIcon icon={faBus} size={12} />}
                  contents={dataFilter.longestDistanceModes.map(
                    (travelMode: string) => {
                      return {
                        key: travelMode,
                        label: includes(TRAVEL_MODES, travelMode)
                          ? t(`travelMode.${travelMode}`)
                          : travelMode,
                        onClose: (): void => {
                          deleteAttributeFilter(
                            'longestDistanceModes',
                            travelMode
                          );
                        },
                      };
                    }
                  )}
                  relation="or"
                />
                <FilterContentGroup
                  title={t('traveledTime')}
                  icon={<FeatureIcon icon={faStopwatch} size={12} />}
                  contents={
                    dataFilter.traveledTime
                      ? [
                          {
                            key: 'traveledTimes',
                            label: `${formatDuration(
                              dataFilter.traveledTime[0]
                            )}~${formatDuration(dataFilter.traveledTime[1])}`,
                            onClose: (): void =>
                              setRangeFilter('traveledTime', null),
                          },
                        ]
                      : []
                  }
                  relation="or"
                />
                <FilterContentGroup
                  title={t('traveledDistance')}
                  icon={<FeatureIcon icon={faRulerHorizontal} size={12} />}
                  contents={
                    dataFilter.traveledDistance
                      ? [
                          {
                            key: 'traveledDistance',
                            label: `${formatDistance(
                              dataFilter.traveledDistance[0]
                            )}~${formatDistance(dataFilter.traveledDistance[1])}`,
                            onClose: (): void =>
                              setRangeFilter('traveledDistance', null),
                          },
                        ]
                      : []
                  }
                  relation="or"
                />
                <FilterContentGroup
                  title={t('departureTime')}
                  icon={<FeatureIcon icon={faClock} size={12} />}
                  contents={
                    dataFilter.depTime
                      ? [
                          {
                            key: 'departureTime',
                            label: `${formatTime(
                              dataFilter.depTime[0]
                            )}~${formatTime(dataFilter.depTime[1])}`,
                            onClose: (): void =>
                              setRangeFilter('depTime', null),
                          },
                        ]
                      : []
                  }
                  relation="or"
                />
                <FilterContentGroup
                  title={t('arrivalTime')}
                  icon={<FeatureIcon icon={faClock} size={12} />}
                  contents={
                    dataFilter.arrTime
                      ? [
                          {
                            key: 'arrivalTime',
                            label: `${formatTime(
                              dataFilter.arrTime[0]
                            )}~${formatTime(dataFilter.arrTime[1])}`,
                            onClose: (): void =>
                              setRangeFilter('arrTime', null),
                          },
                        ]
                      : []
                  }
                  relation="or"
                />
                {tripFilters.map(({ type, labelKey, icon }): ReactNode => {
                  if (
                    dataFilter[type].areaKeyCodes.length === 0 &&
                    dataFilter[type].customAreaKeys.length === 0 &&
                    (type !== 'pass' || dataFilter[type].wayIds.length === 0)
                  ) {
                    return null;
                  }

                  return (
                    <FilterContentGroup
                      key={type}
                      title={t(labelKey)}
                      icon={<FeatureIcon icon={icon} size={12} />}
                      contents={[
                        ...dataFilter[type].areaKeyCodes.map(
                          (keyCode: string) => {
                            return {
                              key: `${type}-area-${keyCode}`,
                              label: findArea(keyCode)?.s_name ?? '',
                              onClose: (): void => {
                                deleteAreaFilter(keyCode, type);
                              },
                            };
                          }
                        ),
                        ...dataFilter[type].customAreaKeys.map(
                          (key: string) => {
                            return {
                              key: `${type}-custom-area-${key}`,
                              label: getCustomAreaByKey(key)?.label || '',
                              onClose: (): void => {
                                deleteCustomAreaFilter(key, type);
                              },
                            };
                          }
                        ),
                        ...(type === 'pass'
                          ? dataFilter[type].wayIds.map((wayId: string) => {
                              const link = links.get(wayId);
                              return {
                                key: `${type}-link-${wayId}`,
                                label: link?.way_name || link?.way_id || '',
                                onClose: (): void => {
                                  deleteLinkFilter(wayId, type);
                                },
                              };
                            })
                          : []),
                      ]}
                      relation={
                        labelKey === 'tripPass' && dataFilter.passRelation
                          ? dataFilter.passRelation
                          : 'or'
                      }
                      onChangeRelation={
                        labelKey === 'tripPass'
                          ? (relation: 'and' | 'or'): void => {
                              setPassRelation(relation);
                            }
                          : undefined
                      }
                    />
                  );
                })}
                <FilterContentGroup
                  title={t('activityTime')}
                  icon={<FeatureIcon icon={faStopwatch} size={12} />}
                  contents={
                    dataFilter.activityTime
                      ? [
                          {
                            key: 'activityTime',
                            label: `${formatDuration(
                              dataFilter.activityTime[0]
                            )}~${formatDuration(dataFilter.activityTime[1])}`,
                            onClose: (): void =>
                              setRangeFilter('activityTime', null),
                          },
                        ]
                      : []
                  }
                  relation="or"
                />
                <FilterContentGroup
                  title={t('stayingTime')}
                  icon={<FeatureIcon icon={faUsers} size={12} />}
                  contents={
                    dataFilter.stayingTime
                      ? [
                          {
                            key: 'stayingTime',
                            label: `${formatTime(
                              dataFilter.stayingTime[0]
                            )}~${formatTime(dataFilter.stayingTime[1])}`,
                            onClose: (): void =>
                              setRangeFilter('stayingTime', null),
                          },
                        ]
                      : []
                  }
                  relation="or"
                />
                <FilterContentGroup
                  title={t('passageTime')}
                  icon={<FeatureIcon icon={faClock} size={12} />}
                  contents={
                    dataFilter.passageTime
                      ? [
                          {
                            key: 'passageTime',
                            label: `${formatTime(
                              dataFilter.passageTime[0]
                            )}~${formatTime(dataFilter.passageTime[1])}`,
                            onClose: (): void =>
                              setRangeFilter('passageTime', null),
                          },
                        ]
                      : []
                  }
                  relation="or"
                />
              </Space>
            </div>
          )}
          {advancedFilterCount > 0 && (
            <div>
              <FilterHeader>{t('advancedAnalysis')}</FilterHeader>
              <FilterContentGroup
                title={t('stopBy')}
                icon={<FeatureIcon icon={faMapMarkerAlt} size={12} />}
                contents={
                  dataFilter.stopBy
                    ? [
                        {
                          key: 'stopBy',
                          label:
                            dataFilter.stopBy === 'after'
                              ? t('stopByAfter')
                              : dataFilter.stopBy === 'before'
                                ? t('stopByBefore')
                                : t('all'),
                          onClose: (): void => setStopBy(null),
                        },
                      ]
                    : []
                }
                relation="or"
              />
            </div>
          )}
        </Space>
      </StyledPanel>
    </Collapse>
  );
};

const StyledPanel = styled(Collapse.Panel)`
  .ant-collapse-content {
    max-height: 40vh;
    overflow-y: auto;
    ${scrollbarStyle}
  }
  .ant-collapse-content-box {
    display: flex;
    flex-direction: row;
    padding: 12px;
    gap: 4px;
  }
`;

const FilterGroupingBracket = styled.div`
  font-size: 10px;
  opacity: 0.4;
  font-weight: bold;
  white-space: nowrap;
  text-transform: uppercase;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  &::before {
    content: '';
    flex-grow: 1;
    display: block;
    width: 8px;
    height: 8px;
    border-top: 2px solid #c8c8c8;
    border-left: 2px solid #c8c8c8;
  }
  &::after {
    content: '';
    flex-grow: 1;
    display: block;
    width: 8px;
    height: 8px;
    border-left: 2px solid #c8c8c8;
    border-bottom: 2px solid #c8c8c8;
  }
`;

const FilterHeader = styled.div`
  color: #8c8c8c;
  font-weight: 600;
`;

export { SideMenuFilter };
